import { withTransaction } from '@elastic/apm-rum-react'
import { Button, DakenService, LPList, LogService, MySquid, Select, Input, ToastHelper, UserHelper, onConfigChange } from '@hub/shared'
import User, { LandingPage } from '@hub/shared/interfaces/user.interface'
import { SqCallout, SqLoader, SqModal } from '@squidit/react-css'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import './delete.page.scoped.scss'

const DeletePage = ({ impersonateUser = null }: any) => {
  const dakenService = useMemo(() => new DakenService(), [])
  const logService = useMemo(() => new LogService(), [])
  const userHelper = useMemo(() => new UserHelper(), [])
  const toast = useMemo(() => new ToastHelper().toast, [])
  logService.init('Delete Page')

  const [user, setUser] = useState<User | null>(null)
  const [config, setConfig] = useState<MySquid | null>(null)
  const [moreWhitelabels, setMoreWhitelabels] = useState(0)
  const [squidWhitelabels, setSquidWhitelabels] = useState<LandingPage[]>([])
  const [textButtonLeft, setTextButtonLeft] = useState('')
  const [textButtonRight, setTextButtonRight] = useState('')
  const [motivation, setMotivation] = useState('')
  const [otherMotivation, setOtherMotivation] = useState('')
  const [functionToButtonLeft, setFunctionToButtonLeft] = useState(() => (event: any) => {})
  const [functionToButtonRight, setFunctionToButtonRight] = useState(() => (event: any) => {})
  const [openModalDelete, setOpenModalDelete] = useState(false)
  const [activeFunction, setActiveFunction] = useState(() => (event: any) => {})
  const [accountDeletionLoader, setAccountDeletionLoader] = useState(false)
  const onConfigChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [loading, setLoading] = useState(false)

  const btnActions = useRef<HTMLDivElement>(null)

  const { t } = useTranslation('deletePage')

  const isOtherMotivation = useMemo(() => {
    return (t('deletionMotivations', { returnObjects: true }) as any).find((motivation: any) => motivation?.other)?.value === motivation
  }, [motivation, t])

  const isDisabled = useMemo(() => {
    return (
      (isOtherMotivation && !otherMotivation) ||
      (!isOtherMotivation && !motivation) ||
      (!!impersonateUser && !impersonateUser?.impersonateAdmin)
    )
  }, [isOtherMotivation, motivation, otherMotivation, impersonateUser])

  const getInfluencerProfileWhitelabel = useCallback(
    async (email: string): Promise<void> => {
      setLoading(true)
      const data = await dakenService.getProfileWhiteLabel(email)

      const whitelabels = data?.landingPagesInfos || []

      const hasHub = whitelabels?.findIndex((whitelabel) => whitelabel._id === '')
      let list: any = []

      if (hasHub >= 0) {
        list = [...list, whitelabels[hasHub]]
        whitelabels.splice(hasHub, 1)
      }

      list = [...list, ...whitelabels]

      setSquidWhitelabels(list)
      setMoreWhitelabels(0)

      setLoading(false)
    },
    [dakenService]
  )

  const executeAccountDeletion = useCallback(
    async (all: boolean = false) => {
      try {
        setAccountDeletionLoader(true)
        const _motivation = isOtherMotivation ? otherMotivation : motivation
        if (_motivation) {
          const { squidId } = user as User
          if (all) {
            await dakenService.deleteProfileAccount(squidId, _motivation, 'true')
          } else {
            await dakenService.deleteProfileAccount(squidId, _motivation)
          }
          setOpenModalDelete(false)
          userHelper.deleteUser()
          setTimeout(() => {
            history.pushState(null, '', '/login?login_message=removed_success')
          }, 500)
        } else {
          setOpenModalDelete(false)
        }
      } catch (error: any) {
        setOpenModalDelete(false)
        logService.error({ message: 'Failed to delete', error })
        const message = error?.data ? error?.data?.message : 'removedFailed'
        toast.error(t(message), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      } finally {
        setAccountDeletionLoader(false)
      }
    },
    [user, isOtherMotivation, motivation, otherMotivation, logService, userHelper, toast, t, dakenService]
  )

  const checkChildWidthIsGreaterThanParent = useCallback((child: any, parent: any): boolean => {
    const parentPadding = parseInt(window.getComputedStyle(parent).width)
    return child.scrollWidth + 20 >= parentPadding
  }, [])

  useLayoutEffect(() => {
    const measure = () => {
      const buttons = btnActions.current

      if (buttons?.childNodes.length) {
        const btnFirst = buttons.childNodes[0]
        const btnSecond = buttons.childNodes[1]

        if (!config) {
          if (numberOfWhitelabels > 1) {
            if (
              checkChildWidthIsGreaterThanParent(btnFirst.firstChild, btnFirst) ||
              checkChildWidthIsGreaterThanParent(btnSecond.firstChild, btnSecond)
            ) {
              setTextButtonLeft(t('deleteAllShortned'))
              setTextButtonRight(t('removeOnlyFromThisShortned'))
            }
          }
        }
      }
    }

    measure()
    window.addEventListener('resize', measure)

    return () => {
      window.removeEventListener('resize', measure)
    }
  })

  useEffect(() => {
    userHelper.getUser().then(async (u) => {
      setUser(u)
      if (u?.profileadditionalinfo?.email) {
        await getInfluencerProfileWhitelabel(u.profileadditionalinfo.email)
      }
      setLoading(false)
    })
    onConfigChangeSubscription.current = onConfigChange.subscribe((configChanged: MySquid | any) => {
      if (configChanged?._id) {
        setConfig(configChanged)
      }
    })
    return () => {
      onConfigChangeSubscription.current?.unsubscribe()
    }
  }, [userHelper, getInfluencerProfileWhitelabel])

  const cancelActionDelete = useCallback(() => {
    setMotivation('')
    setOpenModalDelete(false)
  }, [])

  const numberOfWhitelabels = useMemo(() => squidWhitelabels?.length + moreWhitelabels, [squidWhitelabels, moreWhitelabels])

  useEffect(() => {
    if (!config) {
      if (numberOfWhitelabels === 1) {
        setTextButtonLeft(t('cancel'))
        setFunctionToButtonLeft(() => () => cancelActionDelete())

        setTextButtonRight(t('delete'))
        setFunctionToButtonRight(() => () => executeAccountDeletion(true))
      } else if (numberOfWhitelabels > 1) {
        setTextButtonLeft(t('deleteAll'))
        setFunctionToButtonLeft(() => () => executeAccountDeletion(true))

        setTextButtonRight(t('deleteOnlyFromThis'))
        setFunctionToButtonRight(() => () => executeAccountDeletion())
      }
    } else {
      setTextButtonLeft(t('cancel'))
      setFunctionToButtonLeft(() => () => cancelActionDelete())

      setTextButtonRight(t('delete'))
      setFunctionToButtonRight(() => () => executeAccountDeletion(squidWhitelabels?.length === 1))
    }
  }, [squidWhitelabels, config, cancelActionDelete, executeAccountDeletion, t, numberOfWhitelabels])

  if (loading) {
    return (
      <div className="delete-page loader-wrapper">
        <SqLoader size="big" />
      </div>
    )
  }

  const FooterModal = () => (
    <div className="menu-modal col-24">
      <Button
        onClick={cancelActionDelete}
        id="cancel-button-navigation-modal"
        color="transparent"
        textColor="var(--text_color)"
        borderColor="none"
        disabled={!motivation || accountDeletionLoader}
      >
        <span className="text-button" style={{ whiteSpace: 'nowrap', display: 'inline-block', width: '100%' }}>
          {t('cancel')}
        </span>
      </Button>
      <Button
        onClick={activeFunction}
        id="save-button-navigation-modal"
        className="save-button-navigation"
        disabled={!motivation || (!!impersonateUser && !impersonateUser?.impersonateAdmin)}
        loading={accountDeletionLoader}
        style={{ display: 'flex', flexWrap: 'nowrap', justifyContent: 'center', alignItems: 'center' }}
      >
        <span className="text-button" style={{ whiteSpace: 'nowrap', display: 'inline-block', width: '100%' }}>
          {t('delete')}
        </span>
      </Button>
    </div>
  )

  return (
    <div className="delete-page container">
      <main className="main col-24 col-md-14">
        {config ? (
          <span className="pb-7">{t('messageToExcludeInLP')}</span>
        ) : (
          <div>
            <span>{squidWhitelabels.length > 1 ? t('hasRegisteredInPlural') : t('hasRegisteredIn')}</span>
            <LPList
              squidWhitelabels={squidWhitelabels}
              setSquidWhitelabels={setSquidWhitelabels}
              moreWhitelabels={moreWhitelabels}
              setMoreWhitelabels={setMoreWhitelabels}
            />
          </div>
        )}
        <Select
          label={t('selectMotivation')}
          placeholder={t('selectMotivation')}
          value={motivation}
          name={'motivation-delete-register'}
          id={'motivation-delete-register'}
          onChange={(motivation: string) => setMotivation(motivation)}
          required={true}
        >
          {(t('deletionMotivations', { returnObjects: true }) as any)?.map(({ key, text, value }: any) => (
            <option key={key} value={encodeURIComponent(value)}>
              {text}
            </option>
          ))}
        </Select>
        {isOtherMotivation && (
          <Input
            label={t('inputOtherMotivation')}
            placeholder={t('inputOtherMotivation')}
            value={otherMotivation}
            name={'other-motivation-delete-register'}
            id={'other-motivation-delete-register'}
            onChange={(otherMotivation: string) => setOtherMotivation(otherMotivation)}
            required={true}
          />
        )}
      </main>
      <div className="menu col-24" ref={btnActions}>
        <Button
          onClick={() => {
            if (!config) {
              if (numberOfWhitelabels > 1) {
                setOpenModalDelete(true)
                setActiveFunction(() => functionToButtonLeft)
              } else {
                cancelActionDelete()
              }
            } else {
              cancelActionDelete()
            }
          }}
          id="cancel-button-navigation"
          color="transparent"
          textColor="var(--text_color)"
          borderColor="var(--gray_light)"
          disabled={isDisabled}
        >
          <span className="text-button" style={{ whiteSpace: 'nowrap', display: 'inline-block', width: '100%' }}>
            {textButtonLeft}
          </span>
        </Button>
        <Button
          onClick={() => {
            setOpenModalDelete(true)
            setActiveFunction(() => functionToButtonRight)
          }}
          id="save-button-navigation"
          className="save-button-navigation"
          disabled={isDisabled}
        >
          <span className="text-button" style={{ whiteSpace: 'nowrap', display: 'inline-block', width: '100%' }}>
            {textButtonRight}
          </span>
        </Button>
      </div>
      <SqModal
        open={openModalDelete}
        onCloseChange={() => setOpenModalDelete(false)}
        buttonClose
        header={<h5>{t('deleteConfirmation')}</h5>}
        footer={<FooterModal />}
      >
        <SqCallout title={t('caution')}>{t('warningMessageDelete')}</SqCallout>
      </SqModal>
    </div>
  )
}

export default withTransaction('DeletePage', 'component')(DeletePage)
